/**
 * Browser logging adapter using pino/browser
 * Provides consistent logging interface with optional beacon transport
 */
import pino, { LoggerOptions } from 'pino';
import type { Logger, LoggerFactory, LoggerConfig, LogContext, LogLevel } from '../logging/types.js';

export interface BrowserLoggerConfig extends LoggerConfig {
  /** Endpoint to send logs via beacon/fetch */
  beaconUrl?: string;
  /** Minimum level to send to beacon (default: 'warn') */
  beaconLevel?: LogLevel;
  /** Include logs in console (default: true) */
  console?: boolean;
  /** Application/service name for log context */
  serviceName?: string;
}

const LOG_LEVEL_VALUES: Record<LogLevel, number> = {
  trace: 10,
  debug: 20,
  info: 30,
  warn: 40,
  error: 50,
  fatal: 60,
};

// Browser-specific pino options type
type PinoBrowserOptions = NonNullable<LoggerOptions['browser']>;

interface LogEvent {
  ts: number;
  messages: unknown[];
  bindings: Record<string, unknown>[];
}

class PinoBrowserLoggerAdapter implements Logger {
  constructor(private readonly pinoLogger: pino.Logger) {}

  trace(msg: string, context?: LogContext): void {
    context ? this.pinoLogger.trace(context, msg) : this.pinoLogger.trace(msg);
  }

  debug(msg: string, context?: LogContext): void {
    context ? this.pinoLogger.debug(context, msg) : this.pinoLogger.debug(msg);
  }

  info(msg: string, context?: LogContext): void {
    context ? this.pinoLogger.info(context, msg) : this.pinoLogger.info(msg);
  }

  warn(msg: string, context?: LogContext): void {
    context ? this.pinoLogger.warn(context, msg) : this.pinoLogger.warn(msg);
  }

  error(msg: string, context?: LogContext): void {
    context ? this.pinoLogger.error(context, msg) : this.pinoLogger.error(msg);
  }

  fatal(msg: string, context?: LogContext): void {
    context ? this.pinoLogger.fatal(context, msg) : this.pinoLogger.fatal(msg);
  }

  child(bindings: LogContext): Logger {
    return new PinoBrowserLoggerAdapter(this.pinoLogger.child(bindings));
  }
}

/**
 * Create a beacon transport for sending logs to a remote endpoint
 */
function createBeaconTransmit(
  beaconUrl: string,
  beaconLevel: LogLevel,
  serviceName?: string
): PinoBrowserOptions['transmit'] {
  const minLevel = LOG_LEVEL_VALUES[beaconLevel];

  return {
    level: beaconLevel,
    send: (level: string, logEvent: LogEvent) => {
      // Only send if level meets threshold
      if (LOG_LEVEL_VALUES[level as LogLevel] < minLevel) {
        return;
      }

      const payload = {
        level,
        time: logEvent.ts,
        service: serviceName,
        messages: logEvent.messages,
        bindings: logEvent.bindings,
        userAgent: navigator.userAgent,
        url: window.location.href,
      };

      // Use sendBeacon if available (works even on page unload)
      if (navigator.sendBeacon) {
        navigator.sendBeacon(beaconUrl, JSON.stringify(payload));
      } else {
        // Fallback to fetch with keepalive (works during page unload unlike XHR)
        fetch(beaconUrl, {
          method: 'POST',
          body: JSON.stringify(payload),
          headers: { 'Content-Type': 'application/json' },
          keepalive: true,
        }).catch(() => {
          // Silently fail - don't want logging failures to break the app
        });
      }
    },
  };
}

export class BrowserLoggerFactory implements LoggerFactory {
  private level: LogLevel;
  private readonly config: BrowserLoggerConfig;
  private readonly baseLogger: pino.Logger;

  constructor(config: Partial<BrowserLoggerConfig> = {}) {
    this.config = {
      level: config.level ?? 'info',
      console: config.console ?? true,
      beaconLevel: config.beaconLevel ?? 'warn',
      beaconUrl: config.beaconUrl,
      serviceName: config.serviceName,
    };
    this.level = this.config.level;

    const browserOptions: PinoBrowserOptions = {};

    // Configure console output
    if (this.config.console) {
      browserOptions.write = {
        trace: (o: object) => console.debug(o),
        debug: (o: object) => console.debug(o),
        info: (o: object) => console.info(o),
        warn: (o: object) => console.warn(o),
        error: (o: object) => console.error(o),
        fatal: (o: object) => console.error(o),
      };
    }

    // Configure beacon transport
    if (this.config.beaconUrl) {
      browserOptions.transmit = createBeaconTransmit(
        this.config.beaconUrl,
        this.config.beaconLevel!,
        this.config.serviceName
      );
    }

    this.baseLogger = pino({
      level: this.level,
      browser: browserOptions,
    });
  }

  create(name: string, context?: LogContext): Logger {
    const child = this.baseLogger.child({ name, ...context });
    return new PinoBrowserLoggerAdapter(child);
  }

  setLevel(level: LogLevel): void {
    this.level = level;
    this.baseLogger.level = level;
  }

  getLevel(): LogLevel {
    return this.level;
  }
}
